import { fetchServer } from "@/features/utils/fetch-server";
import type { ClientBranding } from "@/features/clients/types";
import { useQuery } from "@tanstack/react-query";

export interface PublicSessionInfo {
  agent: {
    id: string;
    name: string;
  } | null;
  client: {
    id: string;
    name: string;
    address: string;
    logo: string;
  };
  branding?: ClientBranding;
  phoneNumber: string | null;
  webCallEnabled?: boolean; // Whether "call here" (in-browser web audio) is offered
  phoneHandoffEnabled?: boolean; // Whether sign-in may offer "continue by phone" instead of the SMS code
  agents: Record<string, { name: string }>; // Mapping of all agent IDs to names
}

const fetchInfo = async (path: string): Promise<PublicSessionInfo> => {
  const response = await fetchServer(path, { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch session info");
  return response.json();
};

// Fetch session info (agent and client details)
export function usePublicSessionInfo(publicId: string | undefined) {
  return useQuery({
    queryKey: ["public-session-info", publicId],
    queryFn: () => fetchInfo(`/api/sessions/v1/public/${publicId}/info`),
    enabled: !!publicId,
  });
}

/**
 * The same thing from the connector id, for a widget that has been opened but in which
 * nobody has said anything yet — there is no session to ask about, because none was
 * created (see `use-session-handle.ts`). Identical shape on purpose: the chat renders
 * from one or the other without knowing which.
 */
export function usePublicConnectorInfo(connectorId: string | undefined) {
  return useQuery({
    queryKey: ["public-connector-info", connectorId],
    queryFn: () =>
      fetchInfo(`/api/sessions/v1/public/connector/${connectorId}/info`),
    enabled: !!connectorId,
    // The tenant's branding and agent do not change while somebody is looking at them.
    staleTime: 5 * 60 * 1000,
  });
}
